import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, loadStorefront, office, type Storefront } from "./api";
import { setCurrencySymbol } from "./format";
import { useSession, type OfficeUser, type OfficeRole } from "./session";
import type { TillSettingsPublic } from "./types";

/**
 * The shop as the back office sees it: who is signed in, the till settings, whether Paystack is
 * ready, and the store's name, logo and currency. Same shape the pages used inside the till
 * (`useTill().me`, `store`, `refetchMe`), so they read it unchanged.
 */
export type Me = { can?: Record<string, boolean>; shopLimit?: string | null; user: OfficeUser & { role?: OfficeRole }; settings: TillSettingsPublic; paystack?: { ready: boolean; publicKey: string } };

type Ctx = { me: Me | undefined; store: Storefront | null | undefined; refetchMe: () => void; loading: boolean };
const OfficeCtx = createContext<Ctx>({ me: undefined, store: undefined, refetchMe: () => {}, loading: true });

export function TillProvider({ children }: { children: ReactNode }) {
  const s = useSession();
  const store = useQuery({ queryKey: ["storefront"], queryFn: loadStorefront, staleTime: 3600_000 });
  const me = useQuery({ queryKey: ["office-me", s.user?.id], queryFn: () => api<Me>(office("/me")), enabled: !!s.token, staleTime: 60_000 });
  useEffect(() => {
    if (store.data?.currency?.symbol) setCurrencySymbol(store.data.currency.symbol);
    if (store.data?.storeName) document.title = `${store.data.storeName} · Back office`;
  }, [store.data]);
  return <OfficeCtx.Provider value={{ me: me.data, store: store.data, refetchMe: () => void me.refetch(), loading: me.isLoading }}>{children}</OfficeCtx.Provider>;
}

export const useTill = () => useContext(OfficeCtx);

/** What this shop sells: serial-numbered devices (IMEI, warranty) and a website, or neither (counted stock only). */
export function useShop() {
  const s = useContext(OfficeCtx).me?.settings;
  return { serials: s?.serials !== false, website: s?.websiteImport !== false };
}
